import { useState, useEffect, useCallback, useMemo } from 'react';
import { Download } from 'lucide-react';
import { paymentReconApi } from '../../services/paymentReconApi';
import { useToast } from '../ui/Toast';
import { useTableSort } from '../../hooks/useTableSort';
import { SortableTh } from './SortableTh';
import { formatMoney, agingClass } from '../../utils/paymentReconFormat';
import type { ReconInvoice } from '../../types/paymentRecon';

/** Drill into the Invoices tab for one customer, optionally scoped to one age bucket. */
export interface AgingDrilldown {
    customerCode: string | null;
    customerName: string | null;
    bucket: string | null;   // a BucketKey, or null for the whole customer (Total)
}

interface Props {
    canEdit: boolean;
    companyCode: string;
    onDrilldown: (d: AgingDrilldown) => void;
}

/** Aging buckets per the finance policy. 'unknown' (no due date) folds into Current. */
const BUCKETS = [
    { key: 'current', label: 'Current' },
    { key: '1-30', label: '1–30' },
    { key: '31-60', label: '31–60' },
    { key: '61-90', label: '61–90' },
    { key: '90+', label: '90+' },
] as const;

type BucketKey = (typeof BUCKETS)[number]['key'];

interface AgingRow {
    code: string | null;
    name: string;
    buckets: Record<BucketKey, number>;
    total: number;
}

function emptyBuckets(): Record<BucketKey, number> {
    return { current: 0, '1-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
}

export function AgingTab({ companyCode, onDrilldown }: Props) {
    const { toast } = useToast();
    const [invoices, setInvoices] = useState<ReconInvoice[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // All unpaid open items (Open + PartiallyPaid) — the aging universe.
            const res = await paymentReconApi.listInvoices({ companyCode, status: 'open', limit: 2000 });
            setInvoices(res.invoices);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to load aging', 'error');
        } finally {
            setLoading(false);
        }
    }, [companyCode, toast]);

    useEffect(() => { void load(); }, [load]);

    // Aggregate open amount per customer × bucket.
    const rows = useMemo<AgingRow[]>(() => {
        const byCustomer = new Map<string, AgingRow>();
        for (const inv of invoices) {
            const key = inv.CustomerCode || inv.CustomerName || '(unknown)';
            let row = byCustomer.get(key);
            if (!row) {
                row = { code: inv.CustomerCode, name: inv.CustomerName || inv.CustomerCode || '(unknown)', buckets: emptyBuckets(), total: 0 };
                byCustomer.set(key, row);
            }
            const raw = inv.AgingBucket;
            const bucket: BucketKey = (raw === '1-30' || raw === '31-60' || raw === '61-90' || raw === '90+') ? raw : 'current';
            // Credit memos net the customer balance DOWN, in the credit's own age bucket.
            // Stored positive in dfr, but abs-guard so either sign convention nets correctly.
            const amt = inv.DocumentType === 'CreditMemo' ? -Math.abs(inv.OpenAmount) : inv.OpenAmount;
            row.buckets[bucket] += amt;
            row.total += amt;
        }
        return [...byCustomer.values()];
    }, [invoices]);

    const totals = useMemo(() => {
        const t = { buckets: emptyBuckets(), total: 0 };
        for (const r of rows) {
            for (const b of BUCKETS) t.buckets[b.key] += r.buckets[b.key];
            t.total += r.total;
        }
        return t;
    }, [rows]);

    const { sorted, sort, toggleSort } = useTableSort(rows, {
        customer: r => r.name,
        current: r => r.buckets.current,
        '1-30': r => r.buckets['1-30'],
        '31-60': r => r.buckets['31-60'],
        '61-90': r => r.buckets['61-90'],
        '90+': r => r.buckets['90+'],
        total: r => r.total,
    }, { key: 'total', dir: 'desc' });

    const drill = (r: AgingRow, bucket: BucketKey | 'all') =>
        onDrilldown({ customerCode: r.code, customerName: r.name, bucket: bucket === 'all' ? null : bucket });

    return (
        <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-slate-500">Open receivables by customer and age. Click a figure to open the matching invoices.</p>
                <a
                    href={paymentReconApi.exportUrl('invoices', companyCode)}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
                >
                    <Download className="h-4 w-4" /> Excel
                </a>
            </div>

            {loading ? (
                <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">No open receivables.</div>
            ) : (
                <div className="max-h-[65vh] overflow-auto rounded-lg border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-slate-50">
                            <tr>
                                <SortableTh label="Customer" sortKey="customer" sort={sort} onSort={toggleSort} />
                                {BUCKETS.map(b => (
                                    <SortableTh key={b.key} label={b.label} sortKey={b.key} sort={sort} onSort={toggleSort} className="text-right" />
                                ))}
                                <SortableTh label="Total" sortKey="total" sort={sort} onSort={toggleSort} className="text-right" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {sorted.map((r) => (
                                <tr key={r.code ?? r.name} className="hover:bg-slate-50">
                                    <td className="max-w-xs truncate px-3 py-2 text-slate-700" title={r.name}>
                                        <button onClick={() => drill(r, 'all')} className="truncate text-left hover:text-amber-700 hover:underline">{r.name}</button>
                                    </td>
                                    {BUCKETS.map(b => {
                                        const v = r.buckets[b.key];
                                        return (
                                            <td key={b.key} className="whitespace-nowrap px-3 py-2 text-right">
                                                {Math.abs(v) > 0.005
                                                    ? <button onClick={() => drill(r, b.key)} className={`hover:underline ${v < 0 ? 'text-emerald-700' : agingClass(b.key)}`}>{formatMoney(v, 'EUR')}</button>
                                                    : <span className="text-slate-300">–</span>}
                                            </td>
                                        );
                                    })}
                                    <td className={`whitespace-nowrap px-3 py-2 text-right font-medium ${r.total < 0 ? 'text-emerald-700' : 'text-slate-800'}`}>
                                        <button onClick={() => drill(r, 'all')} className="hover:underline">{formatMoney(r.total, 'EUR')}</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="border-t-2 border-slate-200 text-sm font-semibold text-slate-800 [&_td]:sticky [&_td]:bottom-0 [&_td]:bg-slate-50">
                            <tr>
                                <td className="px-3 py-2">Total ({rows.length})</td>
                                {BUCKETS.map(b => (
                                    <td key={b.key} className="whitespace-nowrap px-3 py-2 text-right">{formatMoney(totals.buckets[b.key], 'EUR')}</td>
                                ))}
                                <td className="whitespace-nowrap px-3 py-2 text-right">{formatMoney(totals.total, 'EUR')}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}
